import { useState } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { PageHeader, Empty } from "@/components/Shell";
import { useFetch, fmtDate, Field, Modal, TableWrap } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Save, Send, Upload, Paperclip, CheckCircle2, FileText, Clock, RotateCcw } from "lucide-react";

const STATUS = (L) => ({
  draft: { label: L("Draft", "Brouillon"), cls: "bg-slate-100 text-slate-700 border-slate-300", Icon: FileText },
  submitted: { label: L("Submitted", "Soumis"), cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: Clock },
  needs_revision: { label: L("Needs revision", "À réviser"), cls: "bg-orange-100 text-orange-800 border-orange-300", Icon: RotateCcw },
  validated: { label: L("Validated", "Validé"), cls: "bg-emerald-100 text-emerald-700 border-emerald-300", Icon: CheckCircle2 },
});

const StatusChip = ({ L, status }) => {
  const s = STATUS(L)[status] || STATUS(L).draft;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}><s.Icon className="h-3 w-3" />{s.label}</span>;
};

const Attachments = ({ items }) => (
  (items || []).length === 0 ? <span className="text-xs text-muted-foreground">—</span> : (
    <div className="flex flex-wrap gap-2">
      {items.map((a, i) => (
        <a key={a.path || i} href={`${API}/files/${a.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline" data-testid={`lp-att-${i}`}>
          <Paperclip className="h-3 w-3" />{a.filename || `Pièce ${i + 1}`}
        </a>
      ))}
    </div>
  )
);

const FeedbackDoc = ({ doc, testid }) => (doc ? (
  <a href={`${API}/files/${doc.path}`} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-1 text-xs text-primary underline" data-testid={testid}>
    <Paperclip className="h-3 w-3" />Document joint : {doc.filename || "document"}
  </a>
) : null);

const FeedbackFilePicker = ({ file, setFile }) => (
  <div className="mt-2 flex flex-wrap items-center gap-2">
    <label className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-1.5 text-sm text-primary cursor-pointer hover:bg-muted" data-testid="sup-attach-label">
      <Upload className="h-4 w-4" />Joindre un document
      <input type="file" className="hidden" accept=".pdf,.html,.htm,application/pdf,text/html" data-testid="sup-attach-input"
        onChange={(e) => {
          const f = e.target.files?.[0]; e.target.value = "";
          if (!f) return;
          if (!/\.(pdf|html?)$/i.test(f.name)) { toast.error("Seuls les fichiers PDF ou HTML sont autorisés."); return; }
          if (f.size > 10 * 1024 * 1024) { toast.error("Fichier trop volumineux (10 Mo maximum)."); return; }
          setFile(f);
        }} />
    </label>
    {file ? (
      <span className="inline-flex items-center gap-1 text-xs" data-testid="sup-attach-name"><Paperclip className="h-3 w-3" />{file.name}
        <button type="button" className="ml-1 text-destructive" onClick={() => setFile(null)} data-testid="sup-attach-remove" aria-label="Retirer le document">×</button>
      </span>
    ) : <span className="text-xs text-muted-foreground">PDF ou HTML — un seul fichier (10 Mo max)</span>}
  </div>
);

// ---------------- Teacher: Cahier de bord ----------------
export function TeacherLessonBook() {
  const { L } = useLang();
  const { data: plans, loading, reload } = useFetch(() => api.get("/lesson-plans"));
  const { data: classes } = useFetch(() => api.get("/classes?mine=1"));
  const EMPTY = { title: "", class_id: "", lesson_plan: "", weekly_objectives: "", class_projects: "" };
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ title: p.title, class_id: p.class_id || "", lesson_plan: p.lesson_plan || "", weekly_objectives: p.weekly_objectives || "", class_projects: p.class_projects || "" }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error(L("A title is required", "Un titre est requis")); return; }
    setSaving(true);
    try {
      if (editId) { await api.put(`/lesson-plans/${editId}`, form); toast.success(L("Saved", "Enregistré")); }
      else { const r = await api.post("/lesson-plans", form); setEditId(r.id); toast.success(L("Draft created — you can attach files then submit", "Brouillon créé — joignez des fichiers puis soumettez")); }
      reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const submit = async (id) => {
    try { await api.post(`/lesson-plans/${id}/submit`); toast.success(L("Submitted to the administration", "Soumis à la direction")); setOpen(false); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file || !editId) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); await api.raw.post(`/lesson-plans/${editId}/attachments`, fd); toast.success(L("Attachment added", "Pièce jointe ajoutée")); reload(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setUploading(false); e.target.value = ""; }
  };
  const del = async (id) => { try { await api.delete(`/lesson-plans/${id}`); toast.success(L("Deleted", "Supprimé")); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const current = editId ? (plans || []).find((p) => p.id === editId) : null;
  const locked = current && current.status === "validated";

  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Teaching Logbook", "Cahier de bord")}
        description={L("Write your lesson plans, weekly objectives and class projects, then submit them to the administration.", "Rédigez vos plans de leçons, objectifs de la semaine et projets de classe, puis soumettez-les à la direction.")}
        action={<Button onClick={openNew} data-testid="lp-new-btn"><Plus className="h-4 w-4 mr-1" />{L("New entry", "Nouvelle entrée")}</Button>} />
      {loading ? <Empty title="…" /> : (plans || []).length === 0 ? <Empty title={L("No entries yet", "Aucune entrée")} /> : (
        <TableWrap><Table>
          <TableHeader><TableRow><TableHead>{L("Title", "Titre")}</TableHead><TableHead>{L("Class", "Classe")}</TableHead><TableHead>{L("Status", "Statut")}</TableHead><TableHead>{L("Files", "Fichiers")}</TableHead><TableHead>{L("Updated", "Modifié")}</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{plans.map((p) => (
            <TableRow key={p.id} data-testid={`lp-row-${p.id}`}>
              <TableCell className="font-medium">{p.title}</TableCell>
              <TableCell className="text-sm">{p.class_name || "—"}</TableCell>
              <TableCell><StatusChip L={L} status={p.status} /></TableCell>
              <TableCell><Attachments items={p.attachments} /></TableCell>
              <TableCell className="text-sm">{fmtDate(p.updated_at || p.created_at)}</TableCell>
              <TableCell className="text-right space-x-1 whitespace-nowrap">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`lp-edit-${p.id}`}>{p.status === "validated" ? L("View", "Voir") : L("Edit", "Éditer")}</Button>
                {(p.status === "draft" || p.status === "needs_revision") && <Button size="sm" onClick={() => submit(p.id)} data-testid={`lp-submit-${p.id}`}><Send className="h-4 w-4 mr-1" />{L("Submit", "Soumettre")}</Button>}
                {p.status === "draft" && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(p.id)} data-testid={`lp-del-${p.id}`}>×</Button>}
              </TableCell>
            </TableRow>))}</TableBody>
        </Table></TableWrap>
      )}

      <Modal open={open} onClose={() => setOpen(false)} wide title={editId ? L("Edit entry", "Modifier l'entrée") : L("New logbook entry", "Nouvelle entrée du cahier")}
        footer={<div className="flex gap-2">
          <Button variant="outline" onClick={save} disabled={saving || locked} data-testid="lp-save-btn"><Save className="h-4 w-4 mr-1" />{saving ? "…" : L("Save draft", "Enregistrer")}</Button>
          {editId && (current?.status === "draft" || current?.status === "needs_revision") && <Button onClick={() => submit(editId)} data-testid="lp-submit-modal-btn"><Send className="h-4 w-4 mr-1" />{L("Submit to administration", "Soumettre à la direction")}</Button>}
        </div>}>
        {(current?.feedback || current?.feedback_document) && (
          <div className={`mb-3 rounded-md border p-3 text-sm ${current.status === "needs_revision" ? "border-orange-300 bg-orange-50 text-orange-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`} data-testid="lp-feedback"><b>{L("Administration feedback", "Retour de la direction")} :</b> {current.feedback || ""}
            <FeedbackDoc doc={current.feedback_document} testid="lp-feedback-doc" />
          </div>
        )}
        <Field label={L("Title", "Titre")}>
          <Input value={form.title} disabled={locked} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lp-title-input" placeholder={L("e.g. Week of Nov 4 — Math", "ex. Semaine du 4 nov — Maths")} />
        </Field>
        <Field label={L("Class", "Classe")}>
          <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })} disabled={locked}>
            <SelectTrigger data-testid="lp-class-select"><SelectValue placeholder={L("Select a class (optional)", "Choisir une classe (optionnel)")} /></SelectTrigger>
            <SelectContent>{(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={L("Lesson plan", "Plan de leçon")}>
          <textarea className="w-full min-h-[110px] rounded-md border border-input bg-background p-2 text-sm" disabled={locked} value={form.lesson_plan} onChange={(e) => setForm({ ...form, lesson_plan: e.target.value })} data-testid="lp-plan-input" />
        </Field>
        <Field label={L("Weekly objectives", "Objectifs de la semaine")}>
          <textarea className="w-full min-h-[80px] rounded-md border border-input bg-background p-2 text-sm" disabled={locked} value={form.weekly_objectives} onChange={(e) => setForm({ ...form, weekly_objectives: e.target.value })} data-testid="lp-objectives-input" />
        </Field>
        <Field label={L("Class projects", "Projets de classe")}>
          <textarea className="w-full min-h-[80px] rounded-md border border-input bg-background p-2 text-sm" disabled={locked} value={form.class_projects} onChange={(e) => setForm({ ...form, class_projects: e.target.value })} data-testid="lp-projects-input" />
        </Field>
        {editId ? (
          <Field label={L("Attachments (PDF / images)", "Pièces jointes (PDF / images)")}>
            <Attachments items={current?.attachments} />
            {!locked && (
              <label className="mt-2 inline-flex items-center gap-2 text-sm text-primary cursor-pointer" data-testid="lp-upload-label">
                <Upload className="h-4 w-4" />{uploading ? L("Uploading…", "Envoi…") : L("Add a file", "Ajouter un fichier")}
                <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif" onChange={upload} data-testid="lp-upload-input" />
              </label>
            )}
          </Field>
        ) : <p className="text-xs text-muted-foreground">{L("Save the draft first to attach files.", "Enregistrez d'abord le brouillon pour joindre des fichiers.")}</p>}
      </Modal>
    </div>
  );
}

// ---------------- Master Admin: Supervision Pédagogique ----------------
export function PedagogySupervision() {
  const { L } = useLang();
  const [fClass, setFClass] = useState("");
  const [fStatus, setFStatus] = useState("");
  const { data: plans, loading, reload } = useFetch(() => api.get("/lesson-plans", { params: { class_id: fClass || undefined, status: fStatus || undefined } }), [fClass, fStatus]);
  const { data: classes } = useFetch(() => api.get("/classes"));
  const [view, setView] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [fbFile, setFbFile] = useState(null);
  const uploadDoc = async (id) => {
    if (!fbFile) return;
    const fd = new FormData(); fd.append("file", fbFile);
    await api.raw.post(`/lesson-plans/${id}/feedback-document`, fd);
    setFbFile(null);
  };
  const validate = async (id) => {
    try { await uploadDoc(id); await api.post(`/lesson-plans/${id}/validate`, { feedback }); toast.success(L("Validated", "Validé")); setView(null); setFeedback(""); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const returnForRevision = async (id) => {
    if (!feedback.trim()) { toast.error(L("A comment is required to return", "Un commentaire est requis pour renvoyer")); return; }
    try { await uploadDoc(id); await api.post(`/lesson-plans/${id}/return`, { feedback }); toast.success(L("Returned for revision", "Renvoyé pour révision")); setView(null); setFeedback(""); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Pedagogical Supervision", "Supervision Pédagogique")}
        description={L("All teacher logbook submissions, by class and date. Read and validate each entry.", "Toutes les soumissions du cahier de bord des enseignants, par classe et par date. Lisez et validez chaque entrée.")} />
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="w-56">
          <Select value={fClass || "all"} onValueChange={(v) => setFClass(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="sup-class-filter"><SelectValue placeholder={L("All classes", "Toutes les classes")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{L("All classes", "Toutes les classes")}</SelectItem>{(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={fStatus || "all"} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="sup-status-filter"><SelectValue placeholder={L("All statuses", "Tous les statuts")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{L("All statuses", "Tous")}</SelectItem><SelectItem value="submitted">{L("Submitted", "Soumis")}</SelectItem><SelectItem value="validated">{L("Validated", "Validé")}</SelectItem><SelectItem value="draft">{L("Draft", "Brouillon")}</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      {loading ? <Empty title="…" /> : (plans || []).length === 0 ? <Empty title={L("No submissions", "Aucune soumission")} /> : (
        <TableWrap><Table>
          <TableHeader><TableRow><TableHead>{L("Teacher", "Enseignant")}</TableHead><TableHead>{L("Title", "Titre")}</TableHead><TableHead>{L("Class", "Classe")}</TableHead><TableHead>{L("Status", "Statut")}</TableHead><TableHead>{L("Files", "Fichiers")}</TableHead><TableHead>{L("Date", "Date")}</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{plans.map((p) => (
            <TableRow key={p.id} data-testid={`sup-row-${p.id}`}>
              <TableCell className="font-medium">{p.author_name || "—"}</TableCell>
              <TableCell>{p.title}</TableCell>
              <TableCell className="text-sm">{p.class_name || "—"}</TableCell>
              <TableCell><StatusChip L={L} status={p.status} /></TableCell>
              <TableCell><Attachments items={p.attachments} /></TableCell>
              <TableCell className="text-sm">{fmtDate(p.submitted_at || p.created_at)}</TableCell>
              <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => { setView(p); setFeedback(p.feedback || ""); setFbFile(null); }} data-testid={`sup-open-${p.id}`}>{L("Read", "Lire")}</Button></TableCell>
            </TableRow>))}</TableBody>
        </Table></TableWrap>
      )}
      <Modal open={!!view} onClose={() => setView(null)} wide title={view?.title || ""}
        footer={view && view.status !== "validated" ? <div className="flex gap-2">
          <Button variant="outline" className="text-orange-700 border-orange-300" onClick={() => returnForRevision(view.id)} data-testid="sup-return-btn"><RotateCcw className="h-4 w-4 mr-1" />{L("Return for revision", "Renvoyer pour révision")}</Button>
          <Button onClick={() => validate(view.id)} data-testid="sup-validate-btn"><CheckCircle2 className="h-4 w-4 mr-1" />{L("Validate", "Valider")}</Button>
        </div> : null}>
        {view && (
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">{view.author_name} · {view.class_name || L("No class", "Sans classe")} · <StatusChip L={L} status={view.status} /></p>
            <div><b>{L("Lesson plan", "Plan de leçon")}</b><p className="whitespace-pre-wrap mt-1">{view.lesson_plan || "—"}</p></div>
            <div><b>{L("Weekly objectives", "Objectifs de la semaine")}</b><p className="whitespace-pre-wrap mt-1">{view.weekly_objectives || "—"}</p></div>
            <div><b>{L("Class projects", "Projets de classe")}</b><p className="whitespace-pre-wrap mt-1">{view.class_projects || "—"}</p></div>
            <div><b>{L("Attachments", "Pièces jointes")}</b><div className="mt-1"><Attachments items={view.attachments} /></div></div>
            {view.status !== "validated" && (
              <Field label={L("Feedback (optional)", "Retour (optionnel)")}>
                <textarea className="w-full min-h-[70px] rounded-md border border-input bg-background p-2 text-sm" value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid="sup-feedback-input" />
                <FeedbackFilePicker file={fbFile} setFile={setFbFile} />
                <FeedbackDoc doc={view.feedback_document} testid="sup-current-doc" />
              </Field>
            )}
            {view.status === "validated" && (view.feedback || view.feedback_document) && <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-emerald-800"><b>{L("Feedback", "Retour")} :</b> {view.feedback || ""}<FeedbackDoc doc={view.feedback_document} testid="sup-validated-doc" /></div>}
          </div>
        )}
      </Modal>
    </div>
  );
}
